/**
 * useTaskClassifications - Hook para gerenciamento de classificações de tarefas
 * 
 * Fornece queries e mutations para CRUD de classificações via RPCs server-side.
 * Classificações são vinculadas a uma categoria e podem ter hierarquia (parent_id).
 *
 * v1.1.0 — Classificações GLOBAIS por módulo: nos módulos Retenção/Jurídico a
 * classificação representa o "motivo" e não deve depender da categoria (a tarefa
 * pode avançar de categoria sem perder o motivo). Nesses casos `category_id` é
 * NULL e a classificação vale para todo o módulo (`task_type`).
 *
 * @version 1.1.0
 * RELEASE: 2026-08-21 v1.1.0 - Suporte a classificações globais por módulo (task_type)
 * RELEASE: 2026-04-06 v1.0.0 - Criação do hook
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { useTaskModuleType, type TaskModuleType } from '@/hooks/useTaskModuleType';

// Logger para depuração
const logger = createLogger('useTaskClassifications');

// Tipo da classificação retornada pela RPC
export interface TaskClassification {
  id: string;
  name: string;
  description: string;
  color: string;
  sort_order: number;
  is_active: boolean;
  /** NULL quando a classificação é global do módulo (v1.1.0) */
  category_id: string | null;
  category_name: string | null;
  /** Módulo ao qual a classificação pertence (normal | retention | legal) */
  task_type?: TaskModuleType;
  parent_id: string | null;
  parent_name: string | null;
  children_count: number;
  tasks_count: number;
  created_at: string;
  updated_at: string;
}

// Tipo para criação/atualização de classificação
export interface UpsertTaskClassificationInput {
  p_id?: string;
  /** NULL/omitido cria uma classificação GLOBAL do módulo informado em p_task_type */
  p_category_id?: string | null;
  p_name: string;
  p_description?: string;
  p_color?: string;
  p_sort_order?: number;
  p_is_active?: boolean;
  p_parent_id?: string;
  /** Módulo da classificação global (normal | retention | legal) */
  p_task_type?: TaskModuleType;
}


/**
 * Hook principal para classificações de tarefas
 * @param categoryId - Filtro opcional por categoria
 * @param onlyActive - Se true, retorna apenas classificações ativas
 */
export function useTaskClassifications(categoryId?: string, onlyActive: boolean = false) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  // Módulo corrente (normal | retention | legal) derivado da rota
  const taskType = useTaskModuleType();

  // Query para listar classificações
  const classificationsQuery = useQuery({
    queryKey: ['task-classifications', currentTenant?.id, categoryId, onlyActive, taskType],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const params: Record<string, unknown> = { p_only_active: onlyActive, p_task_type: taskType };
      if (categoryId) params.p_category_id = categoryId;

      const { data, error } = await supabase.rpc('get_task_classifications', params as any);

      if (error) {
        logger.error('Erro ao buscar classificações de tarefas', { error: error.message });
        throw error;
      }

      return (data || []) as TaskClassification[];
    },
    enabled: !!currentTenant?.id,
  });

  // Mutation para criar/atualizar classificação
  const upsertMutation = useMutation({
    mutationFn: async (input: UpsertTaskClassificationInput) => {
      const { data, error } = await supabase.rpc('upsert_task_classification', input as any);

      if (error) {
        logger.error('Erro ao salvar classificação de tarefa', { error: error.message });
        throw error;
      }

      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['task-classifications'] });
      toast.success(variables.p_id ? 'Classificação atualizada com sucesso' : 'Classificação criada com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao salvar classificação: ${error.message}`);
    },
  });

  // Mutation para excluir classificação
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('delete_task_classification', { p_id: id });

      if (error) {
        logger.error('Erro ao excluir classificação de tarefa', { error: error.message });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-classifications'] });
      toast.success('Classificação excluída com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir classificação: ${error.message}`);
    },
  });

  return {
    classifications: classificationsQuery.data || [],
    isLoading: classificationsQuery.isLoading,
    error: classificationsQuery.error,
    refetch: classificationsQuery.refetch,
    upsert: upsertMutation.mutateAsync,
    isUpserting: upsertMutation.isPending,
    remove: deleteMutation.mutateAsync,
    isRemoving: deleteMutation.isPending,
  };
}
